import { useCallback, useSyncExternalStore } from "react";
import { getCurrency, setCurrency } from "../lib/currencySelect";
import { currencySymbol } from "../lib/currencySymbol";

interface CurrencyCookies {
    locales: string | string[] | undefined;
    currency: string;
}

interface UseSelectCurrencyReturn {
    selectedCurrency: CurrencyCookies;
    updateCurrency: (newCurrency: string, newLocales: string) => void;
    currencySymbolString: string | undefined;
}

// Module-level shared store. Previously each component using this hook held its
// own useState, so changing the currency in the header only updated that one
// component — the price displays didn't refresh until a reload. Now every
// consumer subscribes to one store and re-renders together on change.
let snapshot: CurrencyCookies = getCurrency();
const listeners = new Set<() => void>();

const subscribe = (cb: () => void) => {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
};

const getSnapshot = (): CurrencyCookies => snapshot;

const setGlobalCurrency = (currency: string, locales: string) => {
    setCurrency(currency, locales);
    snapshot = { currency, locales }; // new reference → triggers re-render
    listeners.forEach((l) => l());
};

const useSelectCurrency = (): UseSelectCurrencyReturn => {
    const selectedCurrency = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    const updateCurrency = useCallback((newCurrency: string, newLocales: string) => {
        setGlobalCurrency(newCurrency, newLocales);
    }, []);

    const currencySymbolString = currencySymbol(selectedCurrency);

    return {
        selectedCurrency,
        updateCurrency,
        currencySymbolString,
    };
};

export { useSelectCurrency };
